import { defineComponent, h } from 'vue';
import { QMenu, QBtn, QIcon, QTooltip } from 'quasar';
import BaseComponent from './BaseButtonMixin.jsx';
import './css/BasicButton.css';
import utils from '../../Shared/utils.jsx';
import EventBus from '../event-bus.js';

import TranslationContainer from './TranslationContainer.jsx';

export default defineComponent({
    name: 'basic-popup-menu',
    mixins: [BaseComponent],
    components: { QMenu, QBtn, QIcon, QTooltip },
    data: function () {
        return {
            popupMenuPlacementEval: null,
            popupMenuBackgroundColorEval: null,
            popupMenuFontColorEval: null,
            popupMenuWidthEval: null,
            popupMenuMinHeightEval: null,
            popupMenuMaxHeightEval: null,
            popupMenuBodyPaddingEval: null,
            isDefaultEval: null,
            isdisabledeval: null,

            menushowing: false,
            /** 'start' (left) or 'end' (right); chosen before open from viewport space */
            menuHorizontalAlign: 'start',
            menuAnchorId: `basic-popup-menu-anchor-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`,
        }
    },
    props: {},
    computed: {
        /** Quasar QMenu anchor/self: first word must be top/center/bottom, second left/middle/right/start/end */
        menuAnchor() {
            const p = (this.popupMenuPlacement || 'bottom').toLowerCase();
            const h = this.menuHorizontalAlign === 'end' ? 'end' : 'start';
            if (p === 'top' || p === 'right-top') return `top ${h}`;
            if (p === 'bottom') return `bottom ${h}`;
            if (p === 'left') return 'top start';
            if (p === 'right') return 'top end';
            return `bottom ${h}`;
        },
        menuSelf() {
            const p = (this.popupMenuPlacement || 'bottom').toLowerCase();
            const h = this.menuHorizontalAlign === 'end' ? 'end' : 'start';
            if (p === 'top' || p === 'right-top') return `bottom ${h}`;
            if (p === 'bottom') return `top ${h}`;
            if (p === 'left') return 'top end';
            if (p === 'right') return 'top start';
            return `top ${h}`;
        },
        popupMenuPlacement() {
            if (this.popupMenuPlacementEval === null && this.controlData.PopupMenuPlacement)
                this.popupMenuPlacementEval = utils.compile(this, this.controlData.PopupMenuPlacement);
            if (this.popupMenuPlacementEval)
                return utils.evaluate(this.popupMenuPlacementEval, this);
            return 'bottom';
        },
        popupMenuBackgroundColor() {
            if (this.popupMenuBackgroundColorEval === null && this.controlData.PopupMenuBackgroundColor)
                this.popupMenuBackgroundColorEval = utils.compile(this, this.controlData.PopupMenuBackgroundColor);
            if (this.popupMenuBackgroundColorEval)
                return utils.evaluate(this.popupMenuBackgroundColorEval, this);
            return '';
        },
        popupMenuFontColor() {
            if (this.popupMenuFontColorEval === null && this.controlData.PopupMenuFontColor)
                this.popupMenuFontColorEval = utils.compile(this, this.controlData.PopupMenuFontColor);
            if (this.popupMenuFontColorEval)
                return utils.evaluate(this.popupMenuFontColorEval, this);
            return '';
        },
        popupMenuWidth() {
            if (this.popupMenuWidthEval === null && this.controlData.PopupMenuWidth)
                this.popupMenuWidthEval = utils.compile(this, this.controlData.PopupMenuWidth);
            if (this.popupMenuWidthEval)
                return utils.evaluate(this.popupMenuWidthEval, this);
            return '';
        },
        popupMenuMinHeight() {
            if (this.popupMenuMinHeightEval === null && this.controlData.PopupMenuMinHeight)
                this.popupMenuMinHeightEval = utils.compile(this, this.controlData.PopupMenuMinHeight);
            if (this.popupMenuMinHeightEval)
                return utils.evaluate(this.popupMenuMinHeightEval, this);
            return '';
        },
        popupMenuMaxHeight() {
            if (this.popupMenuMaxHeightEval === null && this.controlData.PopupMenuMaxHeight)
                this.popupMenuMaxHeightEval = utils.compile(this, this.controlData.PopupMenuMaxHeight);
            if (this.popupMenuMaxHeightEval)
                return utils.evaluate(this.popupMenuMaxHeightEval, this);
            return 'auto';
        },
        /* Vue2 passes PopupMenuMaxHeight straight to v-menu's max-height; QMenu takes the same
           prop. 'auto' is the unset default and is not a valid max-height, so return null and
           let Quasar / white-label CSS cap the menu. Bare numbers get px, as Vuetify did. */
        menuMaxHeight() {
            const value = this.popupMenuMaxHeight;
            if (!value || value === 'auto')
                return null;
            return /^\d+(\.\d+)?$/.test(String(value)) ? `${value}px` : String(value);
        },
        popupMenuBodyPadding() {
            if (this.popupMenuBodyPaddingEval === null && this.controlData.PopupMenuBodyPadding)
                this.popupMenuBodyPaddingEval = utils.compile(this, this.controlData.PopupMenuBodyPadding);
            if (this.popupMenuBodyPaddingEval)
                return utils.evaluate(this.popupMenuBodyPaddingEval, this);
            return '';
        },
        isDefault() {
            if (this.isDefaultEval === null && this.controlData.IsDefault)
                this.isDefaultEval = utils.compile(this, this.controlData.IsDefault);
            if (this.isDefaultEval)
                return utils.evaluate(this.isDefaultEval, this);
            return false;
        },
        isdisabled() {
            if (this.isdisabledeval === null && this.controlData.IsDisabled)
                this.isdisabledeval = utils.compile(this, this.controlData.IsDisabled);
            if (this.isdisabledeval)
                return utils.evaluate(this.isdisabledeval, this);
            return false;
        },
        styles() {
            return { 
                ...this.wrapperStyles, 
                ...this.finalButtonStyles 
            };
        },
        routedStyles() {
            // 1. Base wrapper styles
            const wrapperStyles = {
                ...this.sizeStyle,
                overflow: 'hidden',
                whiteSpace: 'nowrap',
            };
            
            // 2. Base button classes and styles
            const buttonClasses = [this.buttonClasses]; 
            const buttonStyles = { ...this.buttonStyles }; 

            // 3. Get the fully resolved CSS object from your utility
            const resolvedStyles = utils.resolveStyleHints(this.styleHints, this) || {};

            // 4. Explicitly route these to the wrapper, bypassing the inner keywords.
            // 'padding' stays on the wrapper (Vue2 behavior) so it spaces the button
            // rather than squeezing the QBtn's flex-centered content off-center.
            const explicitWrapperKeywords = [
                'align-self', 'justify-self', 'margin', 'flex', 'padding'
            ];

            // 5. These keywords indicate CSS properties that belong ON the Quasar button
            const innerKeywords = [
                'border', 'radius', 'background', 'color', 
                'font', 'text', 'justify', 'align', 'line-height', 'cursor'
            ];

            // 6. Route each CSS property to the wrapper or the button
            for (const [key, value] of Object.entries(resolvedStyles)) {
                const lowerKey = key.toLowerCase();
                
                if (explicitWrapperKeywords.some(keyword => lowerKey.includes(keyword))) {
                    wrapperStyles[key] = value;
                } else if (innerKeywords.some(keyword => lowerKey.includes(keyword))) {
                    buttonStyles[key] = value;
                } else {
                    wrapperStyles[key] = value;
                }
            }

            return { wrapperStyles, buttonStyles, buttonClasses };
        },
        wrapperStyles() {
            return this.routedStyles.wrapperStyles;
        },
        finalButtonStyles() {
            return this.routedStyles.buttonStyles;
        },
        finalButtonClasses() {
            return this.routedStyles.buttonClasses;
        }
    },
    created() {
        // Subscribe to a UIAction_Close event, which then emits the close event
        // which is handled within ActionService.jsx, where it imbeds <dialog-basic-modal>
        this.$on('Action-Close', this.performClose);

        if (this.controlData.Name) {
            this.$on(`Action-Close:${this.controlData.Name}`, this.performClose);
            EventBus.$on(`Action-Close:${this.controlData.Name}`, this.performClose);
        }
    },
    //Mounted Replaced with preRenderComplete
    methods: {
        async performClose(action) {
            utils.log(' ** Close Action Received by BasicPopupMenu **');

            this.menushowing = false;
            this.menuHorizontalAlign = 'start';

            try {
                await utils.success(action);
            } catch (e) { }

            // Complete the promise for the executeAction method
            action.FinishFunc(true);
        },
        estimateMenuWidth() {
            if (this.popupMenuWidth) {
                const match = String(this.popupMenuWidth).match(/^(\d+(?:\.\d+)?)/);
                if (match)
                    return parseFloat(match[1]) + 28;
            }

            const scanControls = (controls) => {
                let max = 0;
                for (const c of controls || []) {
                    const width = c?.ControlData?.SizeOptions?.Width;
                    if (width && (width.Mode === 'Fill' || width.Mode === 'Fixed') && width.Units === 'Pixels' && width.Value)
                        max = Math.max(max, Number(width.Value));
                    if (c?.ControlData?.Controls)
                        max = Math.max(max, scanControls(c.ControlData.Controls));
                }
                return max;
            };

            const fromControls = scanControls(this.controlData.Controls);
            if (fromControls > 0)
                return fromControls + 28;

            return 240;
        },
        prepareMenuHorizontalAlign() {
            const placement = (this.popupMenuPlacement || 'bottom').toLowerCase();
            if (placement !== 'bottom' && placement !== 'top' && placement !== 'right-top') {
                this.menuHorizontalAlign = 'start';
                return;
            }

            const trigger = this.$el?.querySelector('.q-btn');
            if (!trigger) {
                this.menuHorizontalAlign = 'start';
                return;
            }

            const rect = trigger.getBoundingClientRect();
            const viewportWidth = document.body.clientWidth;
            const menuWidth = this.estimateMenuWidth();

            // Prefer left edges aligned unless the menu would run past the viewport.
            this.menuHorizontalAlign = rect.left + menuWidth > viewportWidth - 8 ? 'end' : 'start';
        },
        async menuStateChange(value) {
            if (value)
                this.prepareMenuHorizontalAlign();
            else
                this.menuHorizontalAlign = 'start';

            this.menushowing = value;

            if (value) {
                this.repositionPopupMenu();
                if (this.controlData.Actions?.length)
                    await utils.executeAndCompileAllActions(this.controlData.Actions, null, this);
            }
        },
        repositionPopupMenu() {
            this.$nextTick(() => {
                const menu = this.$refs.popupMenu;
                if (!menu?.updatePosition)
                    return;

                menu.updatePosition();
                requestAnimationFrame(() => menu.updatePosition());
            });
        },
        async doClick(e) {
            e.cancelBubble = true;
            e.stopPropagation();

            // Do not toggle menushowing here. QMenu parent-event (no no-parent-event)
            // owns open/close like PopupMenuButton / Vue2 v-menu activator. Manual
            // model toggles on the same click raced Quasar's first portal show and
            // made the first icon click after mount appear to do nothing.
            if (!this.menushowing)
                this.prepareMenuHorizontalAlign();
        },
        preRenderComplete() {
            this.finishRenderHandler(this);
        },
    },
    render() {
        try {
            if (!this.todisplay)
                return null;

            let content = this.getButtonContent();
            /*
            if (this.text && this.icon) {
                if (this.iconPosition === 'Left') content.push(
                    <v-icon
                        style={{ 'margin-right': this.iconGap + 'px' }}
                        medium={!this.themes.small}
                        class={this.iconClasses} left>
                        {this.icon}
                    </v-icon>
                );
                content.push(<TranslationContainer context={this} value={this.text}></TranslationContainer>);
                if (!this.iconPosition || this.iconPosition === 'Right')
                    content.push(
                        <v-icon
                            style={{ 'margin-left': this.iconGap + 'px' }}
                            medium={!this.themes.small}
                            class={this.iconClasses}
                            right>
                            {this.icon}
                        </v-icon>
                    );
            }
            else if (this.text)
                content.push(<TranslationContainer context={this} value={this.text}></TranslationContainer>);
            else if (this.icon)
                content.push(
                    <v-icon xsize="20px" class={this.iconClasses}>
                        {this.icon}
                    </v-icon>
                );
            */

            let menuContent = []; // utils.generateItemsFromArray(h, this, this.controlData.Controls || [], null, 'BasicPopupMenu');
            if (this.controlData.Controls)
                this.controlData.Controls.forEach((c, index) => {
                    if (!(typeof c === 'object'))
                        return;

                    let DynamicControl = utils.getDynamicComponent(h, c);

                    if (!c.$objectId) c.$objectId = utils.generateUUID();

                    let id = c.$objectId;
                    if (index) id += '_' + index;

                    menuContent.push(
                        <DynamicControl
                            onFinishedRender={(reference) => this.finishRenderHandler(reference)}
                            key={id}
                            type={c.ControlType}
                            name={c.ControlData.Name ? c.ControlData.Name : ''}
                            root={this.root}
                            parentType="BasicPopupMenu"
                            controlData={c.ControlData}
                            controlURL={c.ControlURL}
                            sourceIndex={index}
                            controlName={c.Name}
                            scopeitems={this.scopeitems}
                            controlscope={this.controlscope}
                            cacheControl={c.CacheControl}
                            controlEvents={c.Events}
                        >
                        </DynamicControl>
                    );
                });

            let titleContent = [];
            let title = null;

            if (this.controlData.PopupMenuTitle?.Text && this.controlData.PopupMenuTitle?.Icon) {
                if (this.controlData.PopupMenuTitle?.IconPosition === 'Left') titleContent.push(<QIcon name={this.controlData.PopupMenuTitle.Icon} style={{ marginRight: (this.controlData.PopupMenuTitle.IconGap || 8) + 'px' }} />);
                titleContent.push(<TranslationContainer context={this} value={this.controlData.PopupMenuTitle.Text} />);
                if (!this.controlData.PopupMenuTitle?.IconPosition || this.controlData.PopupMenuTitle?.IconPosition === 'Right') titleContent.push(<QIcon name={this.controlData.PopupMenuTitle.Icon} style={{ marginLeft: (this.controlData.PopupMenuTitle.IconGap || 8) + 'px' }} />);
            }
            else if (this.controlData.PopupMenuTitle?.Text)
                titleContent.push(<TranslationContainer context={this} value={this.controlData.PopupMenuTitle?.Text} />);
            else if (this.controlData.PopupMenuTitle?.Icon)
                titleContent.push(<QIcon name={this.controlData.PopupMenuTitle.Icon} />);

            if (titleContent.length)
                title = <div style={{ borderBottom: '1px solid #46465a', fontWeight: 'bold', padding: '8px' }}>{titleContent}</div>;

            const menuPaneStyle = {
                backgroundColor: this.popupMenuBackgroundColor || undefined,
                width: this.popupMenuWidth || undefined,
                minHeight: this.popupMenuMinHeight || undefined,
            };
            // Default 0 so hover wash is edge-to-edge (CCDEV-17617). Docs can still
            // set PopupMenuBodyPadding when an inset is needed.
            const menuBodyStyle = {
                padding: this.popupMenuBodyPadding || '0',
                color: this.popupMenuFontColor || undefined,
                display: 'flex',
                flexDirection: 'column',
            };
            //<div id={this.menuAnchorId} style={{ display: 'inline-block' }}>

            let tooltip = this.renderButtonTooltip();

            const buttonWithMenu = (
                <QBtn
                    class={[this.finalButtonClasses, this.sentenceCaseButtonClasses]}
                    style={this.finalButtonStyles}
                    color={this.finalButtonStyles.backgroundColor || this.finalButtonStyles.background ? undefined : this.color}
                    align={this.align}
                    fab={this.buttonFab}
                    round={this.buttonFab}
                    flat={this.qBtnFlat}
                    outline={this.qBtnOutline}
                    size={this.size}
                    no-caps
                    is-default={this.isDefault}
                    dense={this.density}
                    onClick={(e) => this.doClick(e)}
                    disable={this.isdisabled === true || this.isdisabled === 'true'}
                >
                    {content}
                    <QMenu
                        ref="popupMenu"
                        class="c-BasicPopupMenu-menuHost"
                        anchor={this.menuAnchor}
                        self={this.menuSelf}
                        max-height={this.menuMaxHeight}
                        modelValue={this.menushowing}
                        onUpdate:modelValue={(val) => this.menuStateChange(val)}
                        transition-duration="0"
                        onBeforeShow={() => this.prepareMenuHorizontalAlign()}
                        onShow={() => this.repositionPopupMenu()}
                    >
                        <div style={menuPaneStyle}>
                            <div class="column" style={menuBodyStyle}>
                                {title}
                                {menuContent}
                            </div>
                        </div>
                    </QMenu>
                    {tooltip}
                </QBtn>
            );

            return (
                <div
                    class={{
                        'c-BasicPopupMenu': true,
                        'c-useLiteralCasing': this.useLiteralCasing,
                        [`c-name-${this.name || 'unnamed'}`]: true,
                        'q-mr-sm': true,
                        'text-button': this.text,
                    }}
                    v-show={this.isvisible}
                    style={this.wrapperStyles}
                >
                    {buttonWithMenu}
                </div>
            );
        }
        catch (e) {
            utils.error('BasicPopupMenu Render failed', e);
            return <div>BasicPopupMenu Failed to Render {e}</div>;
        }
    }
});